import { useState, useRef, useEffect, useCallback, forwardRef, useImperativeHandle } from "react";
import { Menu, Transition } from "@headlessui/react";
import { Fragment } from "react";
import services from "../../API";
import { useSelector } from "react-redux";
import { X, Play, Pause, Loader2, Clock } from "lucide-react";
import "../Loader/Loading";
import GenerationHistorySidebar from "../AiModule/GenerationHistorySidebar";
import AdAssetLibraryModal from "../VideoEditor/components/AdAssetLibraryModal";

const otherLanguages = [
  { value: "es", label: "Spanish (Spain)", country: "Spain" },
  { value: "it", label: "Italian (Italy)", country: "Italy" },
  { value: "pt", label: "Portuguese (Portugal)", country: "Portugal" },
  { value: "nl", label: "Dutch (Netherlands)", country: "Netherlands" },
  { value: "pl", label: "Polish (Poland)", country: "Poland" },
  { value: "sl", label: "Slovenian (Slovenia)", country: "Slovenia" },
  { value: "el", label: "Greek (Greece)", country: "Greece" },
  { value: "cs", label: "Czech (Czech Republic)", country: "Czech Republic" },
  { value: "sv", label: "Swedish (Sweden)", country: "Sweden" },
  { value: "da", label: "Danish (Denmark)", country: "Denmark" },
  { value: "no", label: "Norwegian (Norway)", country: "Norway" },
  { value: "fi", label: "Finnish (Finland)", country: "Finland" },
  { value: "hu", label: "Hungarian (Hungary)", country: "Hungary" },
  { value: "ro", label: "Romanian (Romania)", country: "Romania" },
  { value: "tr", label: "Turkish (Turkey)", country: "Turkey" },
];

const CreateTab = forwardRef(function CreateTab({ setActiveTab }, ref) {
  const loading = useSelector((state) => state.data.loading.studioVoices);

  const loadingSaveCharacter = useSelector(
    (state) => state.data.loading.saveCharacter
  );

  const [characterName, setCharacterName] = useState("");
  const [selectedGender, setSelectedGender] = useState("female");
  const [selectedLanguage, setSelectedLanguage] = useState("en-us");
  const [appearance, setAppearance] = useState("");
  const [selectedVoice, setSelectedVoice] = useState();
  const [characterImage, setCharacterImage] = useState(null);
  const [selectedImages, setSelectedImages] = useState([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [processingMode, setProcessingMode] = useState("create");
  const [characterId, setCharacterId] = useState(null);
  const [maleVoices, setMaleVoices] = useState([]);
  const [femaleVoices, setFemaleVoices] = useState([]);
  const [voices, setVoices] = useState([]);
  const [playingVoice, setPlayingVoice] = useState(null);
  const audioRef = useRef(null);
  const [isAssetModalOpen, setIsAssetModalOpen] = useState(false);

  // History State
  const [showGenerationHistory, setShowGenerationHistory] = useState(false);
  const [historyImages, setHistoryImages] = useState([]);
  const [isLoadingHistory, setIsLoadingHistory] = useState(false);
  const [selectedGenerationImage, setSelectedGenerationImage] = useState(null);

  // Unsaved changes guard
  const [showUnsavedModal, setShowUnsavedModal] = useState(false);
  const pendingTabRef = useRef(null);

  // True while a character has been generated but not yet saved
  const hasUnsavedChanges = isProcessing || (!!characterId && !!characterImage);

  // Intercept tab switching so we can show the confirmation modal
  const handleTabSwitch = useCallback(
    (tab) => {
      if (hasUnsavedChanges) {
        pendingTabRef.current = tab;
        setShowUnsavedModal(true);
      } else {
        setActiveTab(tab);
      }
    },
    [hasUnsavedChanges, setActiveTab]
  );

  // Expose requestTabChange to parent via ref
  useImperativeHandle(ref, () => ({
    requestTabChange: (tab) => handleTabSwitch(tab),
  }), [handleTabSwitch]);

  // Browser refresh / close guard
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (hasUnsavedChanges) {
        e.preventDefault();
        e.returnValue = "";
      }
    };
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [hasUnsavedChanges]);

  const handleShowHistory = async () => {
    if (!characterId) return; // Need a character ID to fetch history

    setShowGenerationHistory(true);
    setIsLoadingHistory(true);
    try {
      const res = await services.getCharacterById(characterId);
      if (res?.data?.variants) {
        setHistoryImages(res.data.variants);
      }
    } catch (error) {
      console.error("Error fetching history:", error);
    } finally {
      setIsLoadingHistory(false);
    }
  };

  const handleHistoryImageSelect = (url) => {
    setCharacterImage(url);
  };

  const createPreview = async () => {
    const baseImgUrl = selectedGenerationImage?.originalUrl || characterImage;
    const variantId = selectedGenerationImage?._id;

    setIsProcessing(true);
    const mode = characterId ? "edit" : "create";
    setProcessingMode(mode);
    setCharacterImage(null); // Clear out the old image

    let uploadedImageUrls = [];
    if (selectedImages.length > 0) {
      // Upload images sequentially to avoid overwhelming the server
      for (const image of selectedImages) {
        if (image.url) {
          uploadedImageUrls.push(image.url);
          continue;
        }

        if (!image.file) continue;

        const formData = new FormData();
        formData.append("file", image?.file);

        const uploadResponse = await services.uploadImage(formData); // Assuming you have an upload service
        if (uploadResponse.statusCode === 201) {
          uploadedImageUrls.push(uploadResponse.data.url); // Adjust based on your API response structure
        }
      }
    }

    let charId = characterId;
    let newVariantId = null;

    try {
      if (charId) {
        let finalReferenceUrls = [...uploadedImageUrls];
        if (baseImgUrl) {
          finalReferenceUrls.unshift(baseImgUrl);
        }

        const body = {
          editPrompt: appearance,
          referenceImageUrls: finalReferenceUrls,
        };
        if (baseImgUrl) {
          body.imageUrl = baseImgUrl;
        }
        if (variantId) {
          body.variantId = variantId;
        }

        const editRes = await services.editCharacter(charId, body);
        newVariantId = editRes?.data?.variantId || editRes?.data?._id;
      } else {
        const body = {
          name: characterName,
          gender: selectedGender,
          language: selectedLanguage,
          prompt: appearance,
          referenceImageUrls: uploadedImageUrls,
        };

        const res = await services.createCharacter(body);
        charId = res.data.character._id;
        setCharacterId(charId);
      }
    } catch (error) {
      console.error("Error creating/editing character:", error);
      setIsProcessing(false);
      return;
    }

    let checkCount = 0;
    const maxChecks = 100;

    const checkStatus = async () => {
      checkCount++;
      try {
        const resImage = await services.getCharacterById(charId);
        const variants = resImage?.data?.variants;
        const currentStatus = resImage.data.status;

        if (mode === "create") {
          const isFinished = currentStatus !== "processing";
          if (isFinished) {
            if (variants && variants.length > 0) {
              setCharacterImage(
                variants[variants.length - 1]?.originalUrl
              );
            }
            setSelectedGenerationImage(null);
            clearInterval(intervalId);
            setIsProcessing(false);
          } else if (checkCount >= maxChecks) {
            clearInterval(intervalId);
            setIsProcessing(false);
          }
        } else {
          // Edit mode
          if (newVariantId && variants) {
            const targetVariant = variants.find(
              (v) => v._id === newVariantId || v.id === newVariantId || v.variantId === newVariantId
            );

            if (targetVariant) {
              if (targetVariant.status === "active") {
                if (targetVariant.originalUrl) {
                  setCharacterImage(targetVariant.originalUrl);
                } else if (variants.length > 0) {
                  setCharacterImage(variants[variants.length - 1]?.originalUrl);
                }
                setSelectedGenerationImage(null);
                clearInterval(intervalId);
                setIsProcessing(false);
              } else if (
                targetVariant.status !== "editing" &&
                targetVariant.status !== "processing"
              ) {
                clearInterval(intervalId);
                setIsProcessing(false);
              } else if (checkCount >= maxChecks) {
                clearInterval(intervalId);
                setIsProcessing(false);
              }
            } else if (checkCount >= maxChecks) {
              clearInterval(intervalId);
              setIsProcessing(false);
            }
          } else {
            // Fallback if no newVariantId
            const isFinished =
              currentStatus !== "editing" && currentStatus !== "processing";

            if (isFinished) {
              if (variants && variants.length > 0) {
                setCharacterImage(variants[variants.length - 1]?.originalUrl);
              }
              setSelectedGenerationImage(null);
              clearInterval(intervalId);
              setIsProcessing(false);
            } else if (checkCount >= maxChecks) {
              clearInterval(intervalId);
              setIsProcessing(false);
            }
          }
        }
      } catch (error) {
        console.error("Error polling character status:", error);
        if (checkCount >= maxChecks) {
          clearInterval(intervalId);
          setIsProcessing(false);
        }
      }
    };

    // Check immediately first, then start polling
    const intervalId = setInterval(checkStatus, 5000); // 5 seconds polling interval
    checkStatus();
  };

  const handleRemoveImage = (imageId) => {
    setSelectedImages((prev) => prev.filter((img) => img.id !== imageId));
  };

  const handleAddImageClick = () => {
    setIsAssetModalOpen(true);
  };

  const handleAssetLibrarySelect = (assets) => {
    const imageAssets = assets.filter(
      (asset) => asset.type === "image" || asset.mimeType?.startsWith("image/")
    );

    const newImages = imageAssets.map((asset) => ({
      id: asset._id || asset.id,
      url: asset.url,
      preview: asset.url,
      name: asset.name,
    }));

    setSelectedImages((prev) => [...prev, ...newImages]);
    setIsAssetModalOpen(false);
  };



  // Voice play/pause functionality
  const handlePlayPause = (voice, e) => {
    e.stopPropagation();

    if (playingVoice === voice.voiceId && audioRef.current) {
      // Pause current audio
      audioRef.current.pause();
      setPlayingVoice(null);
    } else {
      // Stop any currently playing audio
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.currentTime = 0;
      }

      // Play new audio
      const audio = new Audio(voice.previewUrl);
      audioRef.current = audio;
      setPlayingVoice(voice.voiceId);

      audio.play().catch((error) => {
        console.error("Error playing audio:", error);
        setPlayingVoice(null);
      });

      audio.onended = () => {
        setPlayingVoice(null);
      };

      audio.onerror = () => {
        setPlayingVoice(null);
      };
    }
  };

  // Cleanup audio on unmount
  useEffect(() => {
    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
    };
  }, []);

  // useEffect(() => {
  //   getVoices();
  // }, [selectedLanguage]);

  useEffect(() => {
    if (selectedGender === "male") {
      setVoices(maleVoices);
    } else if (selectedGender === "female") {
      setVoices(femaleVoices);
    }
  }, [selectedGender, maleVoices, femaleVoices]);

  const saveCharacter = async () => {
    const body = {
      name: characterName,
      gender: selectedGender,
      language: selectedLanguage,
      // defaultVoice: selectedVoice,
      "baseImage.originalUrl": characterImage,
    };
    await services.saveCharacter(characterId, body);

    // Reset all relevant states to null/empty
    setCharacterName("");
    setSelectedGender(null);
    setSelectedLanguage("en-us");
    setAppearance("");
    setCharacterImage(null);
    setSelectedImages([]);
    setSelectedGenerationImage(null);
    setIsProcessing(false);
    setProcessingMode("create");
    setCharacterId(null);
    setPlayingVoice(null); // Reset playing voice state
    setMaleVoices([]);
    setFemaleVoices([]);
    setVoices([]);

    setActiveTab("library");
  };

  return (
    <>
      {/* {loading ? (
        <Loading />
      ) : ( */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Form Column */}
        <div className="lg:col-span-2">
          <div className="rounded-2xl border border-gray-200 dark:border-white/10 bg-white dark:bg-[#111111] px-8 pt-6 pb-5">
            {/* Step 1 */}
            <div className="mb-8">
              <div className="flex items-center gap-3 mb-6">
                <div className="flex items-center justify-center w-8 h-8 rounded-full text-sm font-bold bg-orange-500 text-white">
                  1
                </div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                  Who do you want to create?
                </h3>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium mb-2 text-gray-700 dark:text-gray-300">
                    Name your character
                  </label>
                  <input
                    type="text"
                    placeholder="Type a name (e.g., Sara)"
                    value={characterName}
                    onChange={(e) => setCharacterName(e.target.value)}
                    className="h-12 w-full px-3 rounded-lg border border-gray-300 dark:border-white/10 bg-white dark:bg-white/5 text-gray-900 dark:text-white placeholder:text-gray-400 dark:placeholder:text-gray-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2 text-gray-700 dark:text-gray-300">
                    Select a Gender
                  </label>
                  <div className="flex rounded-lg border border-gray-300 dark:border-white/10 bg-gray-100 dark:bg-white/5 overflow-hidden">
                    <button
                      onClick={() => setSelectedGender("female")}
                      className={`px-4 py-3 text-sm font-medium transition-all flex items-center justify-center gap-2 flex-1 ${selectedGender === "female"
                        ? "bg-orange-500 text-white"
                        : "text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-white/10"
                        }`}
                    >
                      Female
                    </button>
                    <button
                      onClick={() => setSelectedGender("male")}
                      className={`px-4 py-3 text-sm font-medium transition-all flex items-center justify-center gap-2 flex-1 border-l border-gray-300 dark:border-white/10 ${selectedGender === "male"
                        ? "bg-orange-500 text-white"
                        : "text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-white/10"
                        }`}
                    >
                      Male
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {/* Step 2 */}
            <div className="mb-8">
              <div className="flex items-center gap-3 mb-6">
                <div className="flex items-center justify-center w-8 h-8 rounded-full text-sm font-bold bg-orange-500 text-white">
                  2
                </div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                  What's{" "}
                  {selectedGender === "female"
                    ? "her"
                    : selectedGender === "male"
                      ? "his"
                      : "their"}{" "}
                  primary language?
                </h3>
              </div>

              <Menu as="div" className="relative">
                <Menu.Button className="gap-2 min-w-[240px] justify-between px-4 py-2 rounded-lg border border-gray-200 dark:border-white/10 bg-orange-50/50 dark:bg-orange-500/5 hover:bg-orange-50 dark:hover:bg-orange-500/10 hover:border-gray-300 dark:hover:border-white/20 text-gray-900 dark:text-white flex items-center">
                  {selectedLanguage === "en-us"
                    ? "English (US)"
                    : selectedLanguage === "en-uk"
                      ? "English (UK)"
                      : selectedLanguage === "fr"
                        ? "🇫🇷 French"
                        : selectedLanguage === "de"
                          ? "🇩🇪 German"
                          : otherLanguages.find((l) => l.value === selectedLanguage)
                            ?.label || "Select Language"}
                  <i
                    data-lucide="chevron-down"
                    className="h-4 w-4 opacity-50"
                  ></i>
                </Menu.Button>
                <Transition
                  as={Fragment}
                  enter="transition ease-out duration-100"
                  enterFrom="transform opacity-0 scale-95"
                  enterTo="transform opacity-100 scale-100"
                  leave="transition ease-in duration-75"
                  leaveFrom="transform opacity-100 scale-100"
                  leaveTo="transform opacity-0 scale-95"
                >
                  <Menu.Items className="absolute left-0 mt-2 w-56 origin-top-right rounded-lg border border-gray-200 dark:border-white/10 bg-white dark:bg-[#111111] shadow-lg z-50 max-h-80 overflow-y-auto">
                    <div className="p-1">
                      <Menu.Item>
                        {({ active }) => (
                          <button
                            onClick={() => setSelectedLanguage("en-us")}
                            className={`w-full text-left px-3 py-2 rounded-md text-sm ${active
                              ? "bg-gray-100 dark:bg-white/5 text-gray-900 dark:text-white"
                              : "text-gray-900 dark:text-white"
                              }`}
                          >
                            English (US)
                          </button>
                        )}
                      </Menu.Item>
                      <Menu.Item>
                        {({ active }) => (
                          <button
                            onClick={() => setSelectedLanguage("en-uk")}
                            className={`w-full text-left px-3 py-2 rounded-md text-sm ${active
                              ? "bg-gray-100 dark:bg-white/5 text-gray-900 dark:text-white"
                              : "text-gray-900 dark:text-white"
                              }`}
                          >
                            English (UK)
                          </button>
                        )}
                      </Menu.Item>
                      {otherLanguages.map((lang) => (
                        <Menu.Item key={lang.value}>
                          {({ active }) => (
                            <button
                              onClick={() => setSelectedLanguage(lang.value)}
                              className={`w-full text-left px-3 py-2 rounded-md text-sm ${active
                                ? "bg-gray-100 dark:bg-white/5 text-gray-900 dark:text-white"
                                : "text-gray-900 dark:text-white"
                                }`}
                            >
                              {lang.label}
                            </button>
                          )}
                        </Menu.Item>
                      ))}
                    </div>
                  </Menu.Items>
                </Transition>
              </Menu>
            </div>

            {/* Step 3 */}
            <div className="mb-8">
              <div className="flex items-center gap-3 mb-6">
                <div className="flex items-center justify-center w-8 h-8 rounded-full text-sm font-bold bg-orange-500 text-white">
                  3
                </div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                  What should{" "}
                  {selectedGender === "female"
                    ? "she"
                    : selectedGender === "male"
                      ? "he"
                      : "they"}{" "}
                  look like?
                </h3>
              </div>

              <div className="flex flex-col gap-4 sm:flex-row">
                <div className="min-w-0 w-full sm:flex-1">
                  <label htmlFor="character-appearance" className="sr-only">Describe your character's appearance</label>
                  <textarea
                    id="character-appearance"
                    placeholder="Describe their age, physical features, hairstyle, outfit, energy, facial expression, lighting style, and the environment they’re in (home, office, outdoors, studio, etc.)"
                    value={appearance}
                    rows={5}
                    onChange={(e) => setAppearance(e.target.value)}
                    className="min-h-36 w-full resize-y px-3 py-2 rounded-lg border border-gray-300 dark:border-white/10 bg-white dark:bg-white/5 text-gray-900 dark:text-white placeholder:text-gray-400 dark:placeholder:text-gray-500"
                  />

                  {/* Image Previews */}
                  {selectedImages.length > 0 && (
                    <div className="mt-3">
                      <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
                        Added images ({selectedImages.length})
                      </p>
                      <div className="flex flex-wrap gap-3">
                        {selectedImages.map((image) => (
                          <div key={image.id} className="relative group">
                            <img
                              src={image.preview}
                              alt="Preview"
                              className="w-20 h-20 rounded-lg object-cover border-2 border-gray-200 dark:border-white/10 group-hover:border-orange-300 transition-colors"
                            />
                            <button
                              onClick={() => handleRemoveImage(image.id)}
                              className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 hover:bg-red-600 rounded-full flex items-center justify-center transition-all opacity-0 group-hover:opacity-100 shadow-lg"
                            >
                              <X className="w-3 h-3 text-white" />
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                <div className="rounded-xl p-4 flex w-full flex-col items-center justify-center gap-3 border border-gray-200 bg-gray-50 dark:border-white/10 dark:bg-white/5 sm:w-[220px] sm:flex-shrink-0">
                  {/* <button
                    disabled={
                      !characterName ||
                      !selectedGender ||
                      !appearance ||
                      isProcessing
                    }
                    onClick={createPreview}
                    className="w-full py-3 bg-orange-500 hover:bg-orange-600 disabled:opacity-50 disabled:cursor-not-allowed text-white rounded-xl font-semibold shadow-lg shadow-orange-500/20 flex items-center justify-center gap-2 transition-all"
                  >
                    Generate Preview
                  </button> */}
                  <button
                    disabled={
                      !characterName ||
                      !selectedGender ||
                      !appearance ||
                      isProcessing
                    }
                    onClick={createPreview}
                    className="w-full py-3 bg-orange-500 hover:bg-orange-600 disabled:cursor-not-allowed disabled:opacity-50
             disabled:bg-gray-300 disabled:text-gray-500 disabled:hover:bg-gray-300
             dark:disabled:bg-gray-600 dark:disabled:text-gray-300 dark:disabled:hover:bg-gray-600
             text-white rounded-xl font-semibold  flex items-center justify-center gap-2 transition-all"
                  >
                    {isProcessing && <Loader2 className="animate-spin w-5 h-5" />}
                    {isProcessing
                      ? (processingMode === "create" ? "Generating..." : "Customizing...")
                      : (characterId ? "Customize Character" : "Generate Preview")}
                  </button>

                  <button
                    onClick={handleAddImageClick}
                    className="w-full py-3 border border-gray-200 dark:border-white/10 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-50 dark:hover:bg-white/10 rounded-xl gap-2 justify-center flex items-center transition-all hover:border-orange-300 dark:hover:border-orange-500/50"
                  >
                    Add image elements
                  </button>
                </div>
              </div>
              <p className="ai-disclaimer mt-4">
                <span className="ai-disclaimer__lead">AI can make mistakes.</span>{" "}
                Review your character before using it. Using someone’s likeness requires their permission.
              </p>
            </div>

            {/* // Step 4 - Updated Voice Section with horizontal scroll and click-to-select-play */}
            {false && (<div>
              <div className="flex items-center gap-3 mb-6">
                <div className="flex items-center justify-center w-8 h-8 rounded-full text-sm font-bold bg-orange-500 text-white">
                  4
                </div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                  Select a default voice
                </h3>
              </div>

              <div className="mb-6">
                <div className="flex items-center gap-2 mb-3">
                  <span className="text-xs font-semibold uppercase tracking-wide text-gray-400 dark:text-gray-500">
                    Recommended
                  </span>
                  <i
                    data-lucide="sparkles"
                    className="h-3.5 w-3.5 text-orange-500"
                  ></i>
                </div>

                {/* Horizontal scroll container */}
                {loading ? (
                  <Loader2 className="animate-spin w-5 h-5 text-orange-500" />
                ) : (
                  <div className="w-full overflow-x-auto">
                    <div className="flex min-w-max">
                      {voices?.map((voice, index) => {
                        const isPlaying = playingVoice === voice?.voiceId;
                        const isSelected = selectedVoice === voice?.voiceId;

                        return (
                          <button
                            key={voice?.voiceId}
                            onClick={() => {
                              // Select the voice
                              setSelectedVoice(voice?.voiceId);
                              // Play the voice preview
                              handlePlayPause(voice, {
                                stopPropagation: () => { },
                              });
                            }}
                            className={`group px-4 py-3 text-sm font-medium transition-all relative flex items-center gap-2 flex-shrink-0 ${index !== 0
                              ? "border-l border-gray-300 dark:border-white/10"
                              : ""
                              } ${isSelected
                                ? "bg-orange-500 text-white"
                                : "text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-white/10"
                              }`}
                            style={{ minWidth: "200px" }} // Set a minimum width for each item
                          >
                            {/* Play/Pause Icon - Shows playing state */}
                            <div
                              className={`flex-shrink-0 ${isPlaying
                                ? "opacity-100 scale-110"
                                : "opacity-70"
                                }`}
                            >
                              {isPlaying ? (
                                <Pause className="h-3.5 w-3.5" />
                              ) : (
                                <Play className="h-3.5 w-3.5" />
                              )}
                            </div>

                            <div className="flex flex-col items-start flex-1 min-w-0">
                              <span className="truncate font-semibold">
                                {voice?.name?.trim().substring(0, 6)}
                              </span>
                              <span className="text-xs opacity-75 truncate">
                                {`${voice.gender} ${voice.age?.replace("_", " ") || voice.age
                                  }`}
                              </span>
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Custom scrollbar styling */}
                <style jsx>{`
                  .overflow-x-auto::-webkit-scrollbar {
                    height: 6px;
                  }
                  .overflow-x-auto::-webkit-scrollbar-track {
                    background: transparent;
                  }
                  .overflow-x-auto::-webkit-scrollbar-thumb {
                    background: #f97316;
                    border-radius: 3px;
                  }
                  .overflow-x-auto::-webkit-scrollbar-thumb:hover {
                    background: #ea580c;
                  }
                `}</style>
              </div>
            </div>)}
          </div>
        </div>

        {/* Preview Column */}
        <div className="lg:col-span-1">
          <div className="rounded-2xl border border-gray-200 dark:border-white/10 bg-white dark:bg-[#111111] p-6 sticky top-6 w-full max-w-[380px] mx-auto lg:ml-auto">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-base font-semibold text-gray-900 dark:text-white">
                Preview character
              </h3>
              {characterId && (
                <div
                  onClick={handleShowHistory}
                  className="flex items-center gap-1.5 text-sm font-medium text-orange-500 hover:!text-orange-600 px-2 py-1 rounded-lg hover:!bg-gray-100 dark:hover:!bg-gray-800 transition-colors cursor-pointer"
                >
                  <Clock className="w-4 h-4" />
                  Previous generations
                </div>
              )}
            </div>
            <div
              className={`rounded-2xl overflow-hidden mb-6 relative w-full max-w-[340px] mx-auto ${
                isProcessing ? "bg-white dark:bg-white/5" : "bg-gray-100 dark:bg-gray-800"
              }`}
            >
              {isProcessing ? (
                <div className="relative w-full aspect-[9/16] animate-pulse bg-gray-100 dark:bg-white/5">
                  <div className="absolute inset-0 flex flex-col items-center justify-center gap-3">
                    <div className="w-10 h-10 rounded-full border-2 border-orange-500/30 border-t-orange-500 animate-spin"></div>
                    <p className="text-xs font-medium animate-pulse text-orange-500 dark:text-orange-400">
                      Generating Character...
                    </p>
                  </div>
                </div>
              ) : (
                <>
                  {characterImage ? (
                    <>
                      <img
                        src={characterImage}
                        alt="Character preview"
                        className="w-full aspect-[9/16]"
                      />
                      <div className="absolute top-3 left-3 right-3 flex items-center justify-between">
                        <span className="bg-orange-500 text-white text-xs font-semibold px-2.5 py-1 rounded-full">Character</span>
                        <span className="bg-gray-900/80 text-white text-xs font-medium px-2 py-1 rounded-full">9:16</span>
                      </div>
                      <div className="absolute bottom-0 left-0 right-0 p-4 bg-gradient-to-t from-black/80 to-transparent">
                        <p className="text-white/80 text-xs leading-relaxed">Preview shows character with applied customizations</p>
                      </div>
                    </>
                  ) : (
                    <div className="w-full aspect-[9/16] flex items-center justify-center">
                      <span className="text-sm text-gray-500 dark:text-gray-400">Select a character to preview</span>
                    </div>
                  )}
                </>
              )}
            </div>
            <button
              disabled={
                !characterName ||
                !selectedGender ||
                !appearance ||
                !characterId ||
                loadingSaveCharacter ||
                isProcessing
              }
              className="w-full mt-6 h-12 bg-orange-500 hover:bg-orange-600 disabled:opacity-50 disabled:cursor-not-allowed text-white rounded-xl font-semibold shadow-lg shadow-orange-500/30"
              onClick={saveCharacter}
            >
              {loadingSaveCharacter ? "Saving..." : "Save Character & Continue"}
            </button>
            <p className="text-xs text-center mt-3 text-gray-400 dark:text-gray-500">
              Your character is private to you. No one else can access or use
              it.
            </p>
          </div>
        </div>
      </div>

      {/* Generation History Sidebar */}
      <GenerationHistorySidebar
        showGenerationHistory={showGenerationHistory}
        setShowGenerationHistory={setShowGenerationHistory}
        generatedImages={historyImages}
        loading={isLoadingHistory}
        selectedGenerationImage={selectedGenerationImage}
        setSelectedGenerationImage={setSelectedGenerationImage}
        setCharacterImage={handleHistoryImageSelect}
      />
      {/* )} */}

      {/* Unsaved Changes Modal */}
      {showUnsavedModal && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center">
          {/* Backdrop */}
          <div
            className="absolute inset-0 bg-black/60 backdrop-blur-sm"
            onClick={() => setShowUnsavedModal(false)}
          />
          {/* Dialog */}
          <div className="relative z-10 w-full max-w-md mx-4 rounded-2xl border border-gray-200 dark:border-white/10 bg-white dark:bg-[#1a1a1a] p-6 shadow-2xl">
            <div className="flex items-center justify-center w-14 h-14 rounded-full bg-orange-100 dark:bg-orange-500/10 mx-auto mb-4">
              <svg
                className="w-7 h-7 text-orange-500"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={2}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M12 9v4m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"
                />
              </svg>
            </div>
            <h3 className="text-lg font-semibold text-center text-gray-900 dark:text-white mb-2">
              You have unsaved changes
            </h3>
            <p className="text-sm text-center text-gray-500 dark:text-gray-400 mb-6">
              Your character generation is in progress or not saved yet. If you
              leave now, your progress will be lost.
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setShowUnsavedModal(false)}
                className="flex-1 h-11 rounded-xl border border-gray-200 dark:border-white/10 text-gray-700 dark:text-gray-300 font-medium hover:bg-gray-50 dark:hover:bg-white/5 transition-colors"
              >
                Stay on page
              </button>
              <button
                onClick={() => {
                  setShowUnsavedModal(false);
                  if (pendingTabRef.current) {
                    setActiveTab(pendingTabRef.current);
                    pendingTabRef.current = null;
                  }
                }}
                className="flex-1 h-11 rounded-xl bg-orange-500 hover:bg-orange-600 text-white font-medium transition-colors"
              >
                Leave anyway
              </button>
            </div>
          </div>
        </div>
      )}

      <AdAssetLibraryModal
        isOpen={isAssetModalOpen}
        onClose={() => setIsAssetModalOpen(false)}
        onSelect={handleAssetLibrarySelect}
        allowedTypes={["image"]}
      />
    </>
  );
});

export default CreateTab;
